<div class="employees-page invalid-employees-page">
  <main class="employees-shell">
    <section class="employees-hero" *ngIf="!isLoading">
      <div class="hero-copy">
        <div class="eyebrow">Employee Management</div>
        <h1>Invalid Employees</h1>
        <p>Review employees missing required setup data before they are used in scheduling, payroll, or time booking.</p>
      </div>

      <div class="hero-actions">
        <button class="action-btn primary" type="button" (click)="goToImport()">
          <span class="btn-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <path d="m17 8-5-5-5 5" />
              <path d="M12 3v12" />
            </svg>
          </span>
          Import
        </button>
      </div>
    </section>

    <section class="employees-card">
      <div class="employees-card-header" *ngIf="!isLoading">
        <div class="tabs-shell" role="tablist" aria-label="Employee views">
          <button type="button" class="view-tab" routerLink="/employees">
            Employees
            <span class="tab-count">{{ totalEmployeeCount }}</span>
          </button>

          <button type="button" class="view-tab active" routerLink="/employees/missing-info">
            Invalid Employees
            <span class="tab-count">{{ missingEmployeeCount }}</span>
          </button>
        </div>

        <div class="page-size-control">
          <label for="pageSizeTop">Rows</label>
          <select id="pageSizeTop" [(ngModel)]="pageSize" (change)="changePageSize()">
            <option *ngFor="let size of pageSizeOptions" [value]="size">
              {{ size }}
            </option>
          </select>
        </div>
      </div>

      <div class="employee-filters" *ngIf="!isLoading">
        <label class="search-box">
          <span class="search-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          </span>

          <input
            type="text"
            [(ngModel)]="filters.searchTerm"
            (ngModelChange)="onSearchTermChange($event)"
            placeholder="Search employee name..."
          />
        </label>

        <label class="filter-field">
          <span>Company</span>
          <select [(ngModel)]="filters.companyId" (ngModelChange)="onCompanyChange($event)">
            <option [ngValue]="null">All companies</option>
            <option *ngFor="let company of filterService.filterValues?.companies" [ngValue]="company.id">
              {{ company.name }}
            </option>
          </select>
        </label>

        <label class="filter-field">
          <span>Department</span>
          <select [(ngModel)]="filters.departmentId" (ngModelChange)="onDepartmentChange($event)">
            <option [ngValue]="null">All departments</option>
            <option *ngFor="let dept of filterService.filterValues?.departments" [ngValue]="dept.id">
              {{ dept.code }} - {{ dept.name }}
            </option>
          </select>
        </label>

        <label class="filter-field">
          <span>Status</span>
          <select [(ngModel)]="filters.employmentStatus" (ngModelChange)="onEmploymentStatusChange($event)">
            <option [ngValue]="null">All statuses</option>
            <option *ngFor="let status of employmentStatusOptions" [ngValue]="status">
              {{ status }}
            </option>
          </select>
        </label>

        <button class="clear-filters-btn" type="button" (click)="clearFilters()" [disabled]="!hasActiveFilters">
          Clear
        </button>
      </div>

      <div class="employees-table-wrap">
        <div *ngIf="isLoading" class="loading-overlay">
          <div class="spinner"></div>
        </div>

        <div class="error-state" *ngIf="!isLoading && errorMessage">
          <div class="empty-icon danger" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v5" />
              <path d="M12 16h.01" />
            </svg>
          </div>

          <h3>{{ errorMessage }}</h3>

          <button class="action-btn primary" type="button" (click)="loadEmployees()">
            Try Again
          </button>
        </div>

        <ng-container *ngIf="!isLoading && !errorMessage && pagedEmployees.length > 0">
          <table class="employees-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>ID</th>
                <th>Department</th>
                <th>Company</th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              <tr
                *ngFor="let emp of pagedEmployees"
                [ngClass]="getEmployeeColorClass(emp)"
                (click)="openProfile(emp.id)"
              >
                <td>
                  <div class="employee-main">
                    <div class="avatar" [ngClass]="getEmployeeColorClass(emp)">
                      {{ emp.firstName?.[0] || '?' }}{{ emp.lastName?.[0] || '?' }}
                    </div>

                    <div class="identity">
                      <div class="name-line">
                        {{ getEmployeeName(emp) }}
                      </div>

                      <div class="meta-line">
                        <span>#{{ emp.employeeNumber || 'N/A' }}</span>
                      </div>
                    </div>
                  </div>
                </td>

                <td>
                  <span class="id-pill">{{ emp.employeeNumber || 'N/A' }}</span>
                </td>

                <td>
                  <span *ngIf="emp.department; else noDept">
                    {{ getDepartmentDisplay(emp) }}
                  </span>
                  <ng-template #noDept>
                    <span class="missing-text">No department</span>
                  </ng-template>
                </td>

                <td>
                  <span *ngIf="emp.company; else noCompany">
                    {{ getCompanyDisplay(emp) }}
                  </span>
                  <ng-template #noCompany>
                    <span class="missing-text">No company</span>
                  </ng-template>
                </td>


                <td>
                  <button class="row-action" type="button" (click)="$event.stopPropagation(); openProfile(emp.id)">
                    Review
                    <span aria-hidden="true">→</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="employee-mobile-list">
            <button
              class="employee-mobile-card"
              type="button"
              *ngFor="let emp of pagedEmployees"
              [ngClass]="getEmployeeColorClass(emp)"
              (click)="openProfile(emp.id)"
            >
              <div class="mobile-card-top">
                <div class="employee-main">
                  <div class="avatar" [ngClass]="getEmployeeColorClass(emp)">
                    {{ emp.firstName?.[0] || '?' }}{{ emp.lastName?.[0] || '?' }}
                  </div>

                  <div class="identity">
                    <div class="name-line">{{ getEmployeeName(emp) }}</div>
                    <div class="meta-line">#{{ emp.employeeNumber || 'N/A' }}</div>
                  </div>
                </div>

                <div class="mobile-card-pills">
                  <span class="status-pill" [ngClass]="getStatusClass(emp)">
                    <span class="status-dot"></span>
                    {{ getStatusDisplay(emp) }}
                  </span>

                  <span class="missing-pill">{{ getMissingDataDisplay(emp) }}</span>
                </div>
              </div>

              <div class="mobile-details">
                <div>
                  <span>Department</span>
                  <strong>{{ getDepartmentDisplay(emp) }}</strong>
                </div>

                <div>
                  <span>Company</span>
                  <strong>{{ getCompanyDisplay(emp) }}</strong>
                </div>

                <div>
                  <span>Worker Type</span>
                  <strong>{{ getWorkerTypeDisplay(emp) }}</strong>
                </div>

                <div>
                  <span>Pay Group</span>
                  <strong>{{ getPayGroupDisplay(emp) }}</strong>
                </div>

                <div>
                  <span>Shift</span>
                  <strong>{{ getShiftDisplay(emp) }}</strong>
                </div>

                <div>
                  <span>Schedule</span>
                  <strong>{{ getScheduleGroupDisplay(emp) }}</strong>
                </div>

                <div class="full">
                  <span>Missing Data</span>
                  <strong>{{ getMissingFieldsPreview(emp) }}</strong>
                </div>
              </div>
            </button>
          </div>
        </ng-container>

        <div class="empty-state" *ngIf="!isLoading && !errorMessage && pagedEmployees.length === 0">
          <div class="empty-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </div>

          <h3>No invalid employees found</h3>
          <p>All employees currently have the required setup data or no employees match your filters.</p>

          <button class="clear-filters-btn" type="button" (click)="clearFilters()" [disabled]="!hasActiveFilters">
            Clear Filters
          </button>
        </div>
      </div>

      <div class="table-footer" *ngIf="!isLoading && !errorMessage">
        <div class="footer-meta">
          <span *ngIf="totalCount; else noResults">
            {{ (currentPage - 1) * pageSize + 1 }}–{{ currentPage * pageSize > totalCount ? totalCount : currentPage * pageSize }}
            of <strong>{{ totalCount }}</strong>
          </span>

          <ng-template #noResults>
            0 results
          </ng-template>
        </div>

        <div class="pagination">
          <button type="button" (click)="prevPage()" [disabled]="currentPage === 1">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m15 18-6-6 6-6" />
            </svg>
            Prev
          </button>

          <span class="page-chip">{{ currentPage }} / {{ totalPages }}</span>

          <button type="button" (click)="nextPage()" [disabled]="currentPage === totalPages">
            Next
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  </main>
</div>